import React, { useEffect, useState } from 'react';
import { Event } from '../types/event';
import styles from './OrganizationEventDetail.module.css';
import { 
  IconX, 
  IconCalendarEvent, 
  IconMapPin, 
  IconUsers, 
  IconTag, 
  IconInfoCircle,
  IconEye,
  IconChartBar,
  IconChevronLeft
} from '@tabler/icons-react';
import EventStatsChart from './EventStatsChart';
import { eventService } from '../services/api';

// Définir l'interface ViewStats localement
interface ViewStats {
  totalViews: number;
  uniqueViewers: number;
  viewsByDate?: Array<{date: Date; views: number}>;
}

interface OrganizationEventDetailProps {
  event: Event | null;
  onClose: () => void;
}

const OrganizationEventDetail: React.FC<OrganizationEventDetailProps> = ({ 
  event, 
  onClose 
}) => {
  const [viewStats, setViewStats] = useState<ViewStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchEventStats = async () => {
      if (!event) return;
      
      try {
        setLoading(true);
        const stats = await eventService.getViewStats(event.id);
        setViewStats(stats);
      } catch (error) {
        console.error('Error fetching event stats:', error);
        // En cas d'erreur, utiliser les statistiques de base de l'événement
        setViewStats({
          totalViews: event.viewCount || event._count?.views || 0,
          uniqueViewers: Math.max(1, Math.floor((event.viewCount || event._count?.views || 0) * 0.7)), // Estimation
          viewsByDate: []
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchEventStats();
  }, [event]);
  
  if (!event) return null;
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  // Get formatted date
  const formattedDate = formatDate(event.dateTime);
  
  // Extract date and time parts for display
  const dateParts = formattedDate.split(' à ');
  const dateDisplay = dateParts[0];
  const timeDisplay = dateParts[1] || '';

  // Prepare stats data for the chart
  const statsData = {
    viewCount: viewStats?.totalViews || 0,
    uniqueViewCount: viewStats?.uniqueViewers || 0,
    interestedCount: event._count?.interests || 0,
    savedCount: event._count?.savedBy || 0
  };
  
  return (
    <div className={styles.panel}>
      <div className={styles.panelContent}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onClose} aria-label="Retour">
            <IconChevronLeft size={20} />
            <span>Retour</span>
          </button>
          <button className={styles.closeButton} onClick={onClose} aria-label="Fermer">
            <IconX size={20} />
          </button>
        </div>
        
        <div className={styles.imageContainer}>
          {event.imageUrl ? (
            <img src={event.imageUrl} alt={event.title} className={styles.eventImage} />
          ) : (
            <div className={styles.placeholderImage}>
              <IconCalendarEvent size={48} />
            </div>
          )}
          <div className={styles.categoryBadge}>
            {event.eventCategory ? event.eventCategory.name : 'Autre'}
          </div>
        </div>
        
        <div className={styles.eventContent}>
          <h1 className={styles.title}>{event.title}</h1>
          
          <div className={styles.metaInfo}>
            <div className={styles.metaItem}>
              <IconCalendarEvent size={20} />
              <div>
                <div className={styles.metaLabel}>Date</div>
                <div className={styles.metaValue}>{dateDisplay}</div>
                {timeDisplay && <div className={styles.metaTime}>{timeDisplay}</div>}
              </div>
            </div>
            
            <div className={styles.metaItem}>
              <IconMapPin size={20} />
              <div>
                <div className={styles.metaLabel}>Lieu</div>
                <div className={styles.metaValue}>{event.location}</div>
              </div>
            </div>
            
            <div className={styles.metaItem}>
              <IconUsers size={20} />
              <div>
                <div className={styles.metaLabel}>Participants</div>
                <div className={styles.metaValue}>{event._count?.interests || 0} personnes intéressées</div>
              </div>
            </div>
            
            <div className={styles.metaItem}>
              <IconEye size={20} />
              <div>
                <div className={styles.metaLabel}>Vues</div>
                <div className={styles.metaValue}>
                  {loading ? 'Chargement...' : `${viewStats?.totalViews || 0} (${viewStats?.uniqueViewers || 0} uniques)`}
                </div>
              </div>
            </div>
            
            <div className={styles.metaItem}>
              <IconTag size={20} />
              <div>
                <div className={styles.metaLabel}>Catégorie</div>
                <div className={styles.metaValue}>{event.eventCategory ? event.eventCategory.name : 'Autre'}</div>
              </div>
            </div>
          </div>
          
          <div className={styles.description}>
            <h2 className={styles.sectionTitle}>
              <IconInfoCircle size={20} />
              Description
            </h2>
            <div className={styles.descriptionContent}>
              {event.description || "Aucune description disponible pour cet événement."}
            </div>
          </div>
          
          <div className={styles.statsSection}>
            <h2 className={styles.sectionTitle}>
              <IconChartBar size={20} />
              Statistiques
            </h2>
            {loading ? (
              <div className={styles.loadingStats}>Chargement des statistiques...</div>
            ) : (
              <EventStatsChart stats={statsData} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationEventDetail;
